import type { KLineData } from 'klinecharts'

/**
 * The indicator library's contract.
 *
 * An indicator is identified by `id` + `version`. A saved instance pins both, so a
 * version keeps working unchanged forever; evolving an indicator means adding a
 * new version (a new `defs/<id>/v<N>.meta.ts` + `v<N>.calc.ts`). Exactly one
 * version per id is `listed` (addable from the picker): the latest non-builtin.
 *
 * Version 1 of an id that predates this library is the klinecharts BUILT-IN
 * template, left untouched under its original engine name (`builtin: true`,
 * never listed, no calc chunk). Every other version is ours: typed inputs that
 * use TA-Lib's parameter names, a TA-Lib reference the AI is told about, and a
 * calc that reproduces TA-Lib's output.
 */

export type InputKind = 'int' | 'float' | 'select' | 'bool'

interface InputBase {
  /** TA-Lib parameter name (`timeperiod`, `matype`, …); `p1..pn` for builtin v1. */
  key: string
  label: string
  help?: string
}
export interface IntInput extends InputBase {
  kind: 'int'
  default: number
  min: number
  max?: number
}
export interface FloatInput extends InputBase {
  kind: 'float'
  default: number
  min?: number
  max?: number
  step?: number
}
export interface SelectInput extends InputBase {
  kind: 'select'
  default: number
  /** `value` is what is stored and what the calc receives (TA-Lib's integer enum for matype). */
  options: ReadonlyArray<{ value: number; label: string }>
}
export interface BoolInput extends InputBase {
  kind: 'bool'
  default: boolean
}
export type InputSpec = IntInput | FloatInput | SelectInput | BoolInput

/** Resolved input values keyed by `InputSpec.key`. Bools are stored as 0/1. */
export type InputValues = Record<string, number>

/**
 * `marker` is the candle-pattern plot: the calc emits TA-Lib's raw signal
 * (−100 / 0 / 100) and the chart draws a dot under the bar when it is positive
 * (`color`, bullish) or over it when negative (`downColor`, bearish). The
 * adapter turns that one column into two circle figures, `<key>_bull` and
 * `<key>_bear` (see adapter.ts toRows).
 */
export type PlotKind = 'line' | 'histogram' | 'dots' | 'marker'

export interface OutputSpec {
  /** Result/figure key (`rsi`, `macd`, `hist`). */
  key: string
  /** Legend / colour-row title. */
  label: string
  plot: PlotKind
  /** Default colour (line, dots, histogram "up", or marker bullish). */
  color: string
  /** Histogram: bars below `baseValue`; marker: bearish dots (defaults to `color`). */
  downColor?: string
  /** Histogram only: bar base (default 0). */
  baseValue?: number
}

/** How the AI reproduces this indicator with TA-Lib. `{key}` placeholders are input keys. */
export interface TalibRef {
  functions: readonly string[]
  /** One expression, or one per output key for composites. */
  expr: string | Readonly<Record<string, string>>
  /** Our output key → TA-Lib output name for multi-output functions (macd → macdhist …). */
  outputs?: Readonly<Record<string, string>>
}

/** A display-only horizontal reference line on the indicator's pane (RSI 70/30 …). */
export interface LevelSpec {
  value: number
  style?: 'dashed' | 'solid'
  color?: string
}

/** Picker sections (TradingView's taxonomy, roughly). Order here is display order. */
export type Category = 'trend' | 'momentum' | 'volatility' | 'volume' | 'statistics' | 'cycles' | 'patterns'

export const CATEGORIES: ReadonlyArray<{ id: Category; label: string }> = [
  { id: 'trend', label: 'Trend' },
  { id: 'momentum', label: 'Momentum' },
  { id: 'volatility', label: 'Volatility' },
  { id: 'volume', label: 'Volume' },
  { id: 'statistics', label: 'Statistics' },
  { id: 'cycles', label: 'Cycles' },
  { id: 'patterns', label: 'Candle patterns' },
]

export interface IndicatorDef {
  /** Persisted identity; also the klinecharts name of the builtin v1. */
  id: string
  version: number
  label: string
  /** Engine shortName and legend title; defaults to `id`. */
  shortLabel?: string
  pane: 'main' | 'sub'
  /** Picker section. Required for every non-builtin version (presentation only — adding it to a shipped version is fine). */
  category?: Category
  /** `price` syncs precision to the symbol's; `normal` uses `precision`; `volume` formats big numbers. */
  series: 'price' | 'normal' | 'volume'
  precision?: number
  /** ORDER = klinecharts calcParams order. */
  inputs: readonly InputSpec[]
  /** ORDER = klinecharts figures order. */
  outputs: readonly OutputSpec[]
  levels?: readonly LevelSpec[]
  /** Fixed value range for the pane (template-only; klinecharts cannot override maxValue safely). */
  range?: { min?: number; max?: number }
  /** Required for every non-builtin version. */
  talib?: TalibRef
  /** Addable from the picker. At most one version per id, never a builtin. */
  listed: boolean
  /** Picker sort key within the pane (lower first; default 1000, then label). */
  order?: number
  /** May be recalculated for the last bar only during a replay (bounded look-back). */
  tailSafe: boolean
  /** TA-Lib lookback: the first index at which EVERY output is defined, for these inputs. */
  lookback: (inputs: InputValues) => number
  /** Served by klinecharts' untouched built-in template under `id`; has no calc chunk. */
  builtin?: true
}

/** Column-oriented calc output: one Float64Array per output key, NaN for the lookback prefix. */
export type CalcOutput = Record<string, Float64Array>
export type CalcFn = (bars: readonly KLineData[], inputs: InputValues) => CalcOutput

/** One klinecharts result row: a key is OMITTED where the column is NaN. */
export type Row = Partial<Record<string, number>>
